'use client';
import { FlexibleFormVariant } from './FlexibleFormBase';
import { getMicroservicesCountryCode, submitUsedCarEligibilityRequest } from '@/services/microservices/usedCarEligibility';
import { buildRflzSystemDownMessage, handleRflzResponse } from './rflzResponseHandler';
import {
    clearFlexibleFormSubmitError,
    setFlexibleFormSubmitError,
} from './flexibleFormShared';
import type { FlexibleFormSubmitContext } from './flexibleFormShared';
import { findFlexibleFormField, normalizeFlexibleFormFieldKey } from './flexibleFormFieldMatching';

interface RequiredRflzFieldNames {
    firstName?: string;
    lastName?: string;
    radioIdOrVIN?: string;
    zipCode?: string;
}

const RFLZ_GENERIC_ELIGIBILITY_ERROR =
    'We could not process your eligibility right now. Please verify your details and try again.';

function isStateAndZipField(formField: SiteFormField): boolean {
    return normalizeFlexibleFormFieldKey(formField.fields.type) === 'stateandzip';
}

export function getRequiredRflzFieldNames(formFields: SiteFormField[]): RequiredRflzFieldNames {
    const firstNameField = findFlexibleFormField(
        formFields,
        ({ fieldName, displayName, label }) =>
            fieldName === 'firstname' || displayName === 'firstname' || label === 'firstname',
    );
    const lastNameField = findFlexibleFormField(
        formFields,
        ({ fieldName, displayName, label }) =>
            fieldName === 'lastname' || displayName === 'lastname' || label === 'lastname',
    );
    const radioIdOrVinField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'radioidvin' ||
            fieldName === 'radioidvin' ||
            displayName.includes('radioid') ||
            displayName.includes('vin') ||
            label.includes('radioid') ||
            label.includes('vin'),
    );
    const zipCodeField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'zipcode' ||
            fieldName === 'zipcode' ||
            fieldName === 'zip' ||
            displayName === 'zipcode' ||
            displayName === 'zip' ||
            displayName.includes('zip') ||
            label === 'zipcode' ||
            label === 'zip' ||
            label.includes('zip'),
    );
    const hasCombinedStateAndZipField = formFields.some(isStateAndZipField);

    return {
        firstName: firstNameField?.fields.field_name,
        lastName: lastNameField?.fields.field_name,
        radioIdOrVIN: radioIdOrVinField?.fields.field_name,
        zipCode: zipCodeField?.fields.field_name || (hasCombinedStateAndZipField ? 'zip' : undefined),
    };
}

export function handleRflzEligibilityResponse(
    response: Parameters<typeof handleRflzResponse>[0],
    radioIdOrVIN: string,
    context: FlexibleFormSubmitContext,
) {
    const handledResult = handleRflzResponse(response, radioIdOrVIN);

    if (handledResult.type === 'redirect') {
        if (typeof window !== 'undefined') {
            window.location.assign(handledResult.url);
        }
        return;
    }

    if (handledResult.type === 'message') {
        setFlexibleFormSubmitError(context, handledResult.message);
        return;
    }

    const { setSubmitState } = context;
    clearFlexibleFormSubmitError(context);

    const isSuccessful =
        response.status === 'SUCCESS' &&
        (response.data?.status === undefined || response.data.status === 'SUCCESS');

    if (isSuccessful) {
        setSubmitState('success');
    } else {
        setFlexibleFormSubmitError(context, RFLZ_GENERIC_ELIGIBILITY_ERROR);
    }
}

export function createRflzCustomSubmit(
    rflzFieldNames: RequiredRflzFieldNames | null,
): FlexibleFormVariant['customSubmit'] {
    return async (data, context) => {
        try {
            if (
                !rflzFieldNames ||
                !rflzFieldNames.firstName ||
                !rflzFieldNames.lastName ||
                !rflzFieldNames.radioIdOrVIN ||
                !rflzFieldNames.zipCode
            ) {
                setFlexibleFormSubmitError(
                    context,
                    'The Radio ID/VIN form is missing required field configuration. Please try again later.',
                );
                return;
            }

            const firstName = ((data.get(rflzFieldNames.firstName) as string) || '').trim();
            const lastName = ((data.get(rflzFieldNames.lastName) as string) || '').trim();
            const radioIdOrVIN = ((data.get(rflzFieldNames.radioIdOrVIN) as string) || '').trim();
            const zipCode = ((data.get(rflzFieldNames.zipCode) as string) || '').trim();
            const countryCode = getMicroservicesCountryCode();

            const response = await submitUsedCarEligibilityRequest(countryCode, {
                firstName,
                lastName,
                radioIdOrVIN,
                zipCode,
            });

            handleRflzEligibilityResponse(response, radioIdOrVIN, context);
        } catch (error) {
            console.error('RFLZ submit flow failed', error);
            setFlexibleFormSubmitError(context, buildRflzSystemDownMessage());
        }
    };
}
